/**
 * Reference implementation — auto fit-to-width on mount.
 *
 * Waits for non-zero dimensions and viewport, then fires `fitToWidth`
 * once. A production canvas may want different initial zoom behavior
 * (e.g. fit-to-page, restore last zoom level, or no auto-fit).
 */
import { useEffect } from 'react';
import type { StoreApi } from 'zustand';
import type { CameraSlice } from '../../canvas/core/camera';
import type { ZoomSlice } from '../../canvas/features/zoom';

type InitialFitDeps = CameraSlice & ZoomSlice;

const isReady = (state: InitialFitDeps) => {
  const { dimensions, viewportRect } = state.canvas;
  return (
    dimensions.width > 0 &&
    viewportRect.width > 0 &&
    viewportRect.height > 0 &&
    !!state.zoom.actions?.fitToWidth
  );
};

/**
 * Waits for the document canvas to be ready (non-zero dimensions + viewport),
 * then calls `fitToWidth` once.
 *
 * This is a document-canvas concern — the generic zoom feature only provides
 * the `fitToWidth` action; the decision to auto-fit on mount belongs here.
 *
 * Requires the store to include `ZoomSlice` — removing `createZoomFeature`
 * from the feature list produces a type error at the call site.
 */
export const useInitialFit = (store: StoreApi<InitialFitDeps>) => {
  useEffect(() => {
    if (isReady(store.getState())) {
      store.getState().zoom.actions.fitToWidth();
      return undefined;
    }

    const unsubscribe = store.subscribe(state => {
      if (isReady(state)) {
        unsubscribe();
        state.zoom.actions.fitToWidth();
      }
    });

    return unsubscribe;
  }, [store]);
};
